import { type DefaultValues } from "react-hook-form";
import { z, ZodObject, ZodType } from "zod";
import { Checkbox, Form, FormField, FormFieldSecret } from "../main.js";
import {
  type SmartFormFieldMetadata,
  type SmartFormFieldsMetadata,
} from "./common.js";
import { getZodField, getZodFieldTypeName } from "./zod-helpers.js";

export type SmartFormProps<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Schema extends ZodObject<any>,
> = {
  /**
   * A set of initial values used to pre-fill the form.
   */
  defaultValues?: DefaultValues<z.infer<Schema>> | undefined;
  /**
   * A callback function invoked when a user clicks the "Save" button.
   *
   * The submit button is not displayed by default; you must pass the `onSubmit` props for it to
   * appear.
   */
  onSubmit?: ((data: z.infer<Schema>) => Promise<void> | void) | undefined;
  /**
   * A zod schema that defines the shape of the form.
   *
   * Only ZodObject, ZodString, ZodBoolean, and ZodNumber are supported at this time.
   */
  schema: Schema;
  /**
   * A zod schema that defines additional metadata for the form fields.
   */
  fieldsMeta: SmartFormFieldsMetadata;
};

/**
 * A `SmartForm` wraps a `Form` and automatically generates form fields based on a Zod schema.
 *
 * @example
 * ```tsx
 * import { SmartForm } from "@netlify/sdk/ui/react/components";
 * import { z } from "zod";
 *
 *   const schema = z.object({
 *     apiKey: z.string().trim().min(1),
 *     baseUrl: z.string().trim().url(),
 *     stuffEnabled: z.boolean().optional(),
 *   });
 *
 *  const fieldsMeta = {
 *    apiKey: {
 *      label: "API Key",
 *      helpText: "Put your API key here",
 *      secret: true,
 *    },
 *    baseUrl: {
 *      label: "Base URL",
 *      helpText: "The base URL of your API instance",
 *    },
 *    stuffEnabled: {
 *      label: "Enable Stuff",
 *      helpText: "Should we enable everything?",
 *    },
 *  };
 *
 *   <SmartForm
 *     schema={schema}
 *     fieldsMeta={fieldsMeta}
 *     defaultValues={{ baseUrl: "https://www.example.com" }}
 *     onSubmit={(fields) => console.log("Form submitted.", { fields })}
 *   />
 * ```
 */
export const SmartForm = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Schema extends ZodObject<any>,
>({
  defaultValues,
  onSubmit,
  schema,
  fieldsMeta,
}: SmartFormProps<Schema>): JSX.Element => {
  return (
    <Form defaultValues={defaultValues} onSubmit={onSubmit} schema={schema}>
      {Object.entries(schema.shape as Record<string, ZodType>).map(
        ([key, value], idx) => {
          const zodField = getZodField(value);
          const fieldType = getZodFieldTypeName(zodField);
          if (fieldType === "ZodObject") {
            let nestedFieldsMeta: SmartFormFieldsMetadata | undefined =
              undefined;

            // TODO: Validate fieldsMeta with the Zod schema
            if (key in fieldsMeta) {
              nestedFieldsMeta = fieldsMeta[key] as SmartFormFieldsMetadata;
            }
            return (
              <ZodFormFields
                key={`${idx.toString()}:${key}`}
                name={key}
                zodFieldValue={value}
                fieldsMeta={nestedFieldsMeta}
              />
            );
          } else {
            const fieldMeta = fieldsMeta[key] as
              | SmartFormFieldMetadata
              | undefined;

            return (
              <ZodFormField
                key={`${idx.toString()}:${key}`}
                name={key}
                zodFieldType={fieldType}
                fieldMeta={fieldMeta}
              />
            );
          }
        },
      )}
    </Form>
  );
};

const ZodFormField = ({
  name,
  zodFieldType,
  fieldMeta,
}: {
  name: string;
  zodFieldType: string;
  fieldMeta?: SmartFormFieldMetadata | undefined;
}) => {
  // The names of the fields use dot notion to work with zod schemas
  // For a fallback name we just want to grab the last part of the name
  // For example: `config.someField` becomes `SomeField`
  const match = name.match(`(.+)\\.(.+)`);
  const fallbackLabelName = match?.[2] ?? name;

  const fallbackLabel =
    fallbackLabelName.charAt(0).toUpperCase() + fallbackLabelName.slice(1);

  if (zodFieldType === "ZodString" && fieldMeta?.secret) {
    return (
      <FormFieldSecret
        name={name}
        label={fieldMeta.label}
        helpText={fieldMeta.helpText}
      />
    );
  }

  if (zodFieldType === "ZodString" || zodFieldType === "ZodNumber") {
    return (
      <FormField
        name={name}
        label={fieldMeta?.label ?? fallbackLabel}
        placeholder={fieldMeta?.placeholder}
        helpText={fieldMeta?.helpText}
        type={zodFieldType === "ZodNumber" ? "number" : "text"}
      />
    );
  }

  if (zodFieldType === "ZodBoolean") {
    return (
      <Checkbox
        name={name}
        label={fieldMeta?.label ?? fallbackLabel}
        helpText={fieldMeta?.helpText}
      />
    );
  }

  console.error(
    `FormFieldsFromSchema: Not implemented field type: ${zodFieldType}`,
  );

  return null;
};

const ZodFormFields = ({
  name,
  zodFieldValue,
  fieldsMeta,
}: {
  name: string;
  zodFieldValue: ZodType;
  fieldsMeta?: SmartFormFieldsMetadata | undefined;
}) => {
  const zodField = getZodField(zodFieldValue);
  const fieldType = getZodFieldTypeName(zodField);
  // TODO: Do we want to wrap this in a UI container so you can nest form components visually?
  if (fieldType === "ZodObject") {
    return (
      <>
        {Object.entries(
          (zodFieldValue as z.ZodObject<z.ZodRawShape>).shape,
        ).map(([key, value], idx) => {
          const zodField = getZodField(value as ZodType);
          const fieldType = getZodFieldTypeName(zodField);
          if (fieldType === "ZodObject") {
            let nestedFieldsMeta: SmartFormFieldsMetadata | undefined =
              undefined;

            if (
              fieldsMeta !== undefined &&
              key in fieldsMeta &&
              Object.prototype.hasOwnProperty.call(fieldsMeta, key)
            ) {
              nestedFieldsMeta = fieldsMeta[key] as SmartFormFieldsMetadata;
            }
            return (
              <ZodFormFields
                key={`${idx.toString()}:${key}`}
                name={`${name}.${key}`}
                zodFieldValue={value}
                fieldsMeta={nestedFieldsMeta}
              />
            );
          } else {
            const fieldMeta = fieldsMeta?.[key] as
              | SmartFormFieldMetadata
              | undefined;

            return (
              <ZodFormField
                key={`${idx.toString()}:${key}`}
                name={`${name}.${key}`}
                zodFieldType={fieldType}
                fieldMeta={fieldMeta}
              />
            );
          }
        })}
      </>
    );
  }

  console.error(
    `FormFieldsFromSchema: Not implemented field type: ${fieldType}`,
  );

  return null;
};
